import { useCallback, useMemo } from 'react';
import { Task, FullProject, LogEntry } from '../../types';
import { formatDate, parseDate, todayStr, getTaskDateBucket } from '../../utils/dateUtils';
import {
  canonicalProjectId,
  areProjectIdsMatching,
  isMatchingTaskId,
} from '../../utils/idNormalizer';
import { createProjectSyncTracker, ProjectSyncTracker } from './sync/projectSyncStateTracker';
import {
  ensureProjectMigratedToSortOrder,
  applyBucketTransitionToTask,
  computeTargetSortOrder,
  buildReorderHistoryEntries,
} from './taskPositioningUtils';

export interface UseTaskPositioningProps {
  syncTracker?: ProjectSyncTracker;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  rawProjectsRef?: React.MutableRefObject<FullProject[]>;
  rawTasksRef?: React.MutableRefObject<Task[]>;
  ownedTasksRef?: React.MutableRefObject<Task[]>;
  migratedProjectIdsRef: React.MutableRefObject<Set<string>>;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  saveSnapshot: () => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  syncProjectToFirestore: (project: FullProject, isBackground?: boolean) => Promise<unknown>;
  user: { uid?: string; email?: string | null; displayName?: string | null } | null;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  commitTasks: (newTasks: Task[], logAction?: LogEntry['action'], logDescription?: string) => void;
  getProjectTasksWithCommVorgaenge?: (proj: FullProject, projectsList: FullProject[]) => Task[];
}

export function useTaskPositioning({
  syncTracker: propSyncTracker,
  allProjectsRef,
  rawProjectsRef,
  rawTasksRef,
  ownedTasksRef,
  migratedProjectIdsRef,
  setAllProjects,
  currentProjectId,
  tasks,
  setTasks,
  saveSnapshot,
  markProjectDirty,
  onMarkTaskDirty,
  user,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  commitTasks,
  getProjectTasksWithCommVorgaenge,
}: UseTaskPositioningProps) {
  const tracker = useMemo(
    () =>
      propSyncTracker ??
      createProjectSyncTracker({
        allProjectsRef,
        rawProjectsRef,
        rawTasksRef,
        ownedTasksRef,
      }),
    [propSyncTracker, allProjectsRef, rawProjectsRef, rawTasksRef, ownedTasksRef]
  );

  const batchUpdateTaskPositions = useCallback(
    (updates: Record<string | number, { x: number; y: number }>) => {
      if (Object.keys(updates).length === 0) return;

      saveSnapshot();

      const nextProjects = allProjectsRef.current.map((p) => {
        if (!areProjectIdsMatching(p.id, currentProjectId)) return p;

        const newTasks = p.tasks.map((t) => {
          const update = updates[t.id];
          if (update) {
            return { ...t, x: update.x, y: update.y };
          }
          return t;
        });

        markProjectDirty(p.id);
        return { ...p, tasks: newTasks, lastModified: Date.now() };
      });

      allProjectsRef.current = nextProjects;
      setAllProjects(nextProjects);

      setTasks((prev) =>
        prev.map((t) => {
          const update = updates[t.id];
          if (update) {
            return { ...t, x: update.x, y: update.y };
          }
          return t;
        })
      );
    },
    [allProjectsRef, currentProjectId, markProjectDirty, saveSnapshot, setAllProjects, setTasks]
  );

  const sortTasksByDate = useCallback(() => {
    const sorted = [...tasks].sort((a, b) => {
      const dateA = parseDate(a.start || todayStr).getTime();
      const dateB = parseDate(b.start || todayStr).getTime();
      if (dateA !== dateB) return dateA - dateB;
      if (a.startTime && b.startTime) return a.startTime.localeCompare(b.startTime);
      if (a.startTime) return -1;
      if (b.startTime) return 1;
      return 0;
    });
    commitTasks(sorted);
  }, [tasks, commitTasks]);

  const migrateProjectToSortOrder = useCallback(
    (projectId: string) => {
      if (migratedProjectIdsRef.current.has(projectId)) return;
      const project = allProjectsRef.current.find((p) => p.id === projectId);
      if (
        !project ||
        project.tasks.length === 0 ||
        project.tasks.every((t) => typeof t.sortOrder === 'number')
      )
        return;

      migratedProjectIdsRef.current.add(projectId);
      markProjectDirty(projectId);
      const nextProjects = allProjectsRef.current.map((p) => {
        if (p.id !== projectId) return p;

        const metadata = p.metadata || {};
        const itemOrder = metadata.itemOrder || {};
        const migratedTasks = p.tasks.map((t) => ({ ...t }));

        const orderedIds: string[] = [];
        Object.keys(itemOrder).forEach((bucketId) => {
          if (Array.isArray(itemOrder[bucketId])) {
            orderedIds.push(...itemOrder[bucketId]);
          }
        });

        const uniqueOrderedIds = Array.from(new Set(orderedIds));
        const projectFraction =
          Array.from(p.id).reduce((sum, char) => sum + char.charCodeAt(0), 0) / 10000;

        migratedTasks.forEach((task) => {
          const orderIdx = uniqueOrderedIds.indexOf(String(task.id));
          if (orderIdx !== -1) {
            task.sortOrder = (orderIdx + 1) * 1000 + projectFraction;
          } else {
            task.sortOrder = 1000000 + migratedTasks.indexOf(task) * 10 + projectFraction;
          }
        });

        const newMetadata = { ...metadata };
        delete newMetadata.itemOrder;

        return { ...p, tasks: migratedTasks, metadata: newMetadata, lastModified: Date.now() };
      });

      allProjectsRef.current = nextProjects;
      setAllProjects(nextProjects);
    },
    [setAllProjects, markProjectDirty, allProjectsRef, migratedProjectIdsRef]
  );

  const reorderTask = useCallback(
    (
      taskId: string | number,
      targetProjectId: string,
      targetBucketId: string,
      overId?: string | number,
      position: 'before' | 'after' | 'end' = 'end',
      mode: 'status' | 'date' | 'custom' = 'status',
      explicitPrevSort?: number,
      explicitNextSort?: number,
      sourceProjectId?: string
    ) => {
      const freshToday = formatDate(new Date());
      saveSnapshot();
      const projects = allProjectsRef.current;

      const rawTargetProjectId =
        targetProjectId === 'inbox_inforum'
          ? INBOX_PROJECT_ID
          : targetProjectId === 'comm_internal'
            ? COMM_PROJECT_ID
            : targetProjectId;
      const rawSourceProjectId =
        sourceProjectId === 'inbox_inforum'
          ? INBOX_PROJECT_ID
          : sourceProjectId === 'comm_internal'
            ? COMM_PROJECT_ID
            : sourceProjectId;

      let origSourceProject = rawSourceProjectId
        ? projects.find((p) => p.id === rawSourceProjectId)
        : undefined;
      if (!origSourceProject) {
        origSourceProject = projects.find((p) =>
          p.tasks.some((t) => String(t.id) === String(taskId) || isMatchingTaskId(taskId, t.id, p.id))
        );
      }
      if (!origSourceProject) {
        origSourceProject = projects.find((p) => areProjectIdsMatching(p.id, currentProjectId));
      }
      if (!origSourceProject) return;

      const origTargetProject =
        projects.find((p) => p.id === rawTargetProjectId) || origSourceProject;
      const isSameProject = origSourceProject.id === origTargetProject.id;

      const sourceProject: FullProject = {
        ...origSourceProject,
        tasks: origSourceProject.tasks ? [...origSourceProject.tasks] : [],
        metadata: origSourceProject.metadata ? { ...origSourceProject.metadata } : {},
      };
      const targetProject: FullProject = isSameProject
        ? sourceProject
        : {
            ...origTargetProject,
            tasks: origTargetProject.tasks ? [...origTargetProject.tasks] : [],
            metadata: origTargetProject.metadata ? { ...origTargetProject.metadata } : {},
          };

      const didRenumberSource = ensureProjectMigratedToSortOrder(sourceProject, markProjectDirty);
      let didRenumberTarget = didRenumberSource;
      if (!isSameProject) {
        didRenumberTarget = ensureProjectMigratedToSortOrder(targetProject, markProjectDirty);
      }

      const taskIndex = sourceProject.tasks.findIndex(
        (t) =>
          String(t.id) === String(taskId) || isMatchingTaskId(taskId, t.id, sourceProject.id)
      );
      if (taskIndex === -1) return;

      const [origTask] = sourceProject.tasks.splice(taskIndex, 1);
      const task: Task = { ...origTask };

      const prevStatus = task.status;
      const prevStart = task.start;
      const prevEnd = task.end;
      const prevProjectId = task.projectId || sourceProject.id;
      const currentDateBucket = task.archived ? 'archived' : getTaskDateBucket(task, freshToday);

      applyBucketTransitionToTask({
        task,
        targetBucketId,
        mode,
        currentDateBucket,
        freshToday,
        targetProject,
      });

      task.sortOrder = computeTargetSortOrder({
        taskId,
        targetProject,
        targetBucketId,
        overId,
        position,
        mode,
        freshToday,
        explicitPrevSort: didRenumberTarget ? undefined : explicitPrevSort,
        explicitNextSort: didRenumberTarget ? undefined : explicitNextSort,
      });

      if (sourceProject.id !== targetProject.id) {
        task.projectId = targetProject.id;
      }

      const historyEntries = buildReorderHistoryEntries({
        task,
        prevProjectId,
        targetProject,
        prevStatus,
        prevStart,
        prevEnd,
        userEmail: user?.email || undefined,
      });

      if (historyEntries.length > 0) {
        const existingHistory = task.history || [];
        task.history = [...historyEntries, ...existingHistory].slice(0, 100);
      }

      const now = Date.now();
      sourceProject.lastModified = now;
      markProjectDirty(sourceProject.id);

      const pTasks = isSameProject
        ? [...sourceProject.tasks, task]
        : [...targetProject.tasks, task];

      pTasks.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

      targetProject.tasks = pTasks;
      targetProject.lastModified = now;
      markProjectDirty(targetProject.id);
      onMarkTaskDirty(task.id, targetProject.id);

      const nextProjects = projects.map((p) => {
        if (p.id === targetProject.id) return targetProject;
        if (p.id === sourceProject.id) return sourceProject;
        return p;
      });

      tracker.syncTask(task, targetProject.id);
      tracker.commitProjects(
        nextProjects,
        setAllProjects,
        isSameProject ? [sourceProject.id] : [sourceProject.id, targetProject.id]
      );

      const normSourceId = canonicalProjectId(sourceProject.id);
      const normTargetId = canonicalProjectId(targetProject.id);

      if (
        areProjectIdsMatching(normSourceId, currentProjectId) ||
        areProjectIdsMatching(normTargetId, currentProjectId)
      ) {
        const matchingProj = nextProjects.find((p) =>
          areProjectIdsMatching(p.id, currentProjectId)
        );
        const currentTasks = matchingProj
          ? getProjectTasksWithCommVorgaenge
            ? getProjectTasksWithCommVorgaenge(matchingProj, nextProjects)
            : matchingProj.tasks || []
          : [];
        setTasks(currentTasks);
      }
    },
    [
      allProjectsRef,
      tracker,
      currentProjectId,
      COMM_PROJECT_ID,
      INBOX_PROJECT_ID,
      saveSnapshot,
      markProjectDirty,
      onMarkTaskDirty,
      setAllProjects,
      setTasks,
      user?.email,
      getProjectTasksWithCommVorgaenge,
    ]
  );

  return {
    batchUpdateTaskPositions,
    sortTasksByDate,
    migrateProjectToSortOrder,
    reorderTask,
  };
}
